<template>
  <div class="min-h-screen bg-[#F3F4F6] p-4 pb-20">
    <!-- Header Section -->
    <div class="rounded-3xl px-6 pt-6 pb-8 mb-6">
      <div class="flex justify-between items-start mb-8">
        <!-- Date Section -->
        <div>
          <div class="text-4xl font-bold text-gray-800">{{ formattedDate }}</div>
          <div class="text-4xl font-bold text-gray-800">{{ formattedMonth }}</div>
        </div>

        <!-- Time Section -->
        <div class="text-right">
          <div class="text-lg font-medium text-gray-600">{{ currentDay }}</div>
          <div class="text-2xl font-bold text-gray-800 mt-2">{{ currentTime }}</div>
        </div>
      </div>

      <!-- Greeting -->
      <div class="flex items-center justify-between">
        <div class="text-2xl font-bold text-gray-800">
          Let's Go,<br>{{ userFirstName }}!
        </div>
        <div class="flex-shrink-0 ml-4">
          <img src="/car.webp" alt="Car animation" class="w-20 h-20 car-animation"/>
        </div>
      </div>
    </div>

    <!-- Neumorphic Search Bar -->
    <div class="mb-4">
      <div @click="openSearchAssistant" class="neumorphic-tile rounded-2xl p-3 cursor-pointer transition-all duration-200">
        <div class="flex items-center">
          <svg class="w-4 h-4 text-gray-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <span class="text-gray-500 flex-1 text-sm">Ask anything about travel...</span>
        </div>
      </div>
    </div>

    <!-- Services Grid Section -->
    <div class="space-y-3">
      <!-- Row 1: T1, T2, T3, T4 -->
      <div class="grid grid-cols-4 gap-2">
        <!-- T1 - Train -->
        <div @click="openService('train')" class="neumorphic-tile rounded-2xl p-4 aspect-square flex flex-col items-center justify-center cursor-pointer transition-all duration-200">
          <div class="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-2 neumorphic-icon">
            <Train class="text-gray-600" :size="18" />
          </div>
          <span class="text-xs font-medium text-gray-700">Train</span>
        </div>

        <!-- T2 - Metro -->
        <div @click="openService('metro')" class="neumorphic-tile rounded-2xl p-4 aspect-square flex flex-col items-center justify-center cursor-pointer transition-all duration-200">
          <div class="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-2 neumorphic-icon">
            <Zap class="text-gray-600" :size="18" />
          </div>
          <span class="text-xs font-medium text-gray-700">Metro</span>
        </div>

        <!-- T3 - Flight -->
        <div @click="openService('flights')" class="neumorphic-tile rounded-2xl p-4 aspect-square flex flex-col items-center justify-center cursor-pointer transition-all duration-200">
          <div class="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-2 neumorphic-icon">
            <Plane class="text-gray-600" :size="18" />
          </div>
          <span class="text-xs font-medium text-gray-700">Flights</span>
        </div>

        <!-- T4 - Bus -->
        <div @click="openService('buses')" class="neumorphic-tile rounded-2xl p-4 aspect-square flex flex-col items-center justify-center cursor-pointer transition-all duration-200">
          <div class="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-2 neumorphic-icon">
            <Car class="text-gray-600" :size="18" />
          </div>
          <span class="text-xs font-medium text-gray-700">Bus</span>
        </div>
      </div>

      <!-- Row 2: T5 (Feeds) and T6, T7, T8 -->
      <div class="grid grid-cols-2 gap-2">
        <!-- T5 - Carousel (Left side) -->
        <div class="neumorphic-card rounded-2xl h-46 overflow-hidden relative">
          <!-- Carousel Container -->
          <div class="h-full flex transition-transform duration-300 ease-in-out" :style="{ transform: `translateX(-${currentSlide * 100}%)` }">
            <!-- Hotels Slide -->
            <div @click="openService('hotels')" class="min-w-full h-full flex flex-col items-center justify-center cursor-pointer transition-all duration-200 p-4 neumorphic-slide">
              <div class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-3 neumorphic-icon">
                <Bed class="text-gray-600" :size="24" />
              </div>
              <span class="text-lg font-bold text-gray-800">Hotels</span>
              <span class="text-sm text-gray-600">Find nearby hotels</span>
            </div>

            <!-- Restaurants Slide -->
            <div @click="openService('restaurants')" class="min-w-full h-full flex flex-col items-center justify-center cursor-pointer transition-all duration-200 p-4 neumorphic-slide">
              <div class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-3 neumorphic-icon">
                <UtensilsCrossed class="text-gray-600" :size="24" />
              </div>
              <span class="text-lg font-bold text-gray-800">Restaurants</span>
              <span class="text-sm text-gray-600">Find nearby dining</span>
            </div>

            <!-- Shopping Slide -->
            <div @click="openService('shopping')" class="min-w-full h-full flex flex-col items-center justify-center cursor-pointer transition-all duration-200 p-4 neumorphic-slide">
              <div class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-3 neumorphic-icon">
                <ShoppingBag class="text-gray-600" :size="24" />
              </div>
              <span class="text-lg font-bold text-gray-800">Shopping</span>
              <span class="text-sm text-gray-600">Find nearby stores</span>
            </div>


          </div>

          <!-- Carousel Indicators -->
          <div class="absolute bottom-3 left-1/2 transform -translate-x-1/2 flex space-x-2">
            <button
              v-for="(slide, index) in 3"
              :key="index"
              @click="goToSlide(index)"
              class="w-2 h-1 rounded-full transition-all"
              :class="currentSlide === index ? 'bg-gray-500' : 'bg-gray-200'"
            ></button>
          </div>
        </div>

        <!-- Right side container for T6-T8 (Single Tile) and T9 -->
        <div class="space-y-2">
          <!-- T6-T8 - Weather Tile -->
          <div @click="openWeatherPopup" class="neumorphic-tile rounded-2xl p-3 h-24 flex items-center justify-between cursor-pointer transition-all duration-200">
            <!-- Weather Icon and Temperature -->
            <div class="flex flex-col items-center">
              <div v-if="weatherLoading" class="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-1 neumorphic-icon">
                <Loader2 class="text-gray-600 animate-spin" :size="18" />
              </div>
              <div v-else-if="currentWeather && currentWeather.main && currentWeather.weather" class="flex flex-col items-center">
                <div class="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-1 neumorphic-icon">
                  <component :is="getWeatherIcon(currentWeather.weather[0].main)" class="text-gray-600" :size="18" />
                </div>
                <div class="text-lg font-bold text-gray-800">{{ Math.round(currentWeather.main.temp) }}°</div>
              </div>
              <div v-else-if="weatherError" class="flex flex-col items-center">
                <div class="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-1 neumorphic-icon">
                  <i class="fas fa-exclamation-triangle text-gray-600 text-lg"></i>
                </div>
                <span class="text-xs text-gray-500">Error</span>
              </div>
            </div>

            <!-- Weather Details -->
            <div v-if="currentWeather && currentWeather.main && currentWeather.weather" class="flex flex-col items-end text-right">
              <div class="text-xs font-semibold text-gray-800 capitalize mb-1">{{ currentWeather.weather[0].description }}</div>
              <div class="text-xs text-gray-600 mb-1">Feels {{ Math.round(currentWeather.main.feels_like) }}°</div>
              <div class="text-xs text-gray-600">{{ Math.round(currentWeather.main.humidity) }}% humidity</div>
            </div>
            <div v-else-if="weatherLoading" class="flex flex-col items-end text-right">
              <div class="text-xs text-gray-500">Loading...</div>
            </div>
            <div v-else-if="weatherError" class="flex flex-col items-end text-right">
              <div class="text-xs text-gray-500">Weather unavailable</div>
            </div>
          </div>

          <!-- T9 - Tasks -->
          <div @click="openService('tasks')" class="neumorphic-tile rounded-2xl p-4 h-20 flex flex-col items-center justify-center cursor-pointer transition-all duration-200">
            <div class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-1 neumorphic-icon relative">
              <CheckSquare class="text-gray-600" :size="20" />
              <div class="absolute -top-1 -right-1 bg-teal-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                {{ taskCount }}
              </div>
            </div>
            <span class="text-xs font-medium text-gray-700">Tasks</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Bottom Navigation -->
    <Navbar currentRoute="home" />

    <!-- Service Modals -->
    <div v-if="activeService" class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end">
      <div class="w-full rounded-t-3xl max-h-[90vh] overflow-hidden" style="background-color: #F3F4F6;">
        <!-- Modal Header -->
        <div class="flex justify-between items-center p-4 border-b border-blue-200"
             :class="activeService === 'tasks' ? 'bg-blue-50' : ''"
             :style="activeService !== 'tasks' ? 'background-color: #F3F4F6;' : ''">
          <h3 class="text-lg font-semibold capitalize"
              :class="activeService === 'tasks' ? 'text-blue-800' : 'text-gray-900'">
            {{ activeService === 'tasks' ? 'Task Management' : activeService }}
          </h3>
          <div class="flex items-center space-x-2">
            <!-- Refresh button for tasks -->
            <button v-if="activeService === 'tasks'" @click="refreshTasksData"
                    class="p-2 rounded-full transition-colors hover:bg-blue-100 text-blue-600"
                    title="Refresh tasks">
              <RefreshCw class="w-4 h-4" />
            </button>
            <button @click="closeService" class="p-2 rounded-full transition-colors"
                    :class="activeService === 'tasks' ? 'hover:bg-blue-100 text-blue-600' : 'hover:bg-gray-100 text-gray-500'">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
        </div>

        <!-- Modal Content -->
        <div class="h-96 overflow-y-auto px-4">
          <!-- Hotels Content -->
          <div v-if="activeService === 'hotels'" class="h-full">
            <HotelsInline
              :hotels-data="hotelsData"
              :is-loading="hotelsLoading"
              :error="hotelsError"
              @retry="fetchHotels"
            />
          </div>

          <!-- Shopping Content -->
          <div v-if="activeService === 'shopping'" class="h-full">
            <ShoppingInline
              :shopping-data="shoppingData"
              :is-loading="shoppingLoading"
              :error="shoppingError"
              @retry="fetchShopping"
            />
          </div>

          <!-- Restaurants Content -->
          <div v-if="activeService === 'restaurants'" class="h-full">
            <RestaurantsInline
              :restaurants-data="restaurantsData"
              :is-loading="restaurantsLoading"
              :error="restaurantsError"
              @retry="fetchRestaurants"
            />
          </div>

          <!-- Buses Content -->
          <div v-if="activeService === 'buses'" class="h-full">
            <BusesInline
              :buses-data="busesData"
              :is-loading="busesLoading"
              :error="busesError"
              @retry="fetchBuses"
            />
          </div>

          <!-- Tasks Content -->
          <div v-if="activeService === 'tasks'" class="h-full">
            <TaskManagementEnhanced ref="taskManagement" @task-created="onTaskCreated" @task-deleted="onTaskDeleted" />
          </div>
        </div>
      </div>
    </div>

    <!-- Coming Soon Modal -->
    <ComingSoonModal
      :isVisible="showComingSoonModal"
      @close="showComingSoonModal = false"
    />

    <!-- Weather Popup - Reusable Component -->
    <WeatherPopup
      :isVisible="showWeatherPopup"
      :isMobile="true"
      @close="closeWeatherPopup"
    />

    <!-- Flight Popup - Reusable Component -->
    <FlightSearchPopup
      :isVisible="showFlightPopup"
      :isMobile="true"
      @close="closeFlightPopup"
      @search-flights="handleFlightSearch"
    />

    <!-- Train Popup - Reusable Component -->
    <TrainSearchPopup
      :isVisible="showTrainPopup"
      :isMobile="true"
      @close="closeTrainPopup"
    />

    <!-- Metro Popup - Reusable Component -->
    <MetroSearchPopup
      :isVisible="showMetroPopup"
      :isMobile="true"
      @close="closeMetroPopup"
    />

    <!-- Search Assistant Popup -->
    <div v-if="showSearchAssistant" class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end">
      <div class="w-full h-[85vh] rounded-t-3xl overflow-hidden bg-[#ABBCF4]">
        <!-- Header -->
        <div class="flex items-center justify-between px-4 py-4 bg-[#ABBCF4] sticky top-0 z-20 rounded-t-3xl">
          <button @click="closeSearchAssistant" class="text-white p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd" />
            </svg>
          </button>
          <h2 class="text-xl font-bold text-white">Travley Assistant</h2>
          <div class="flex items-center text-white text-sm">
            <span class="bg-white bg-opacity-20 px-2 py-1 rounded">{{ dailySearchCount }}/3</span>
          </div>
        </div>

        <!-- Chat Container -->
        <div class="flex-1 flex flex-col h-full">
          <!-- Messages Area -->
          <div class="flex-1 overflow-y-auto p-4 space-y-4" style="max-height: calc(85vh - 180px);">
            <!-- Welcome Message -->
            <div v-if="chatMessages.length === 0" class="text-center py-4">
              <div class="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-3">
                <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <h3 class="text-white text-lg font-semibold mb-2">Hi! I'm Travley</h3>

              <!-- Example questions -->
              <div class="space-y-2 text-left max-w-sm mx-auto">
                <p class="text-white text-opacity-60 text-xs mb-2">Try asking:</p>
                <button @click="searchQuery = 'Tell me about Paris'; sendMessage()" class="w-full text-left bg-white bg-opacity-10 rounded-lg p-2 text-white text-sm hover:bg-opacity-20 transition-all">
                  "Tell me about Paris"
                </button>
                <button @click="searchQuery = 'Best time to visit Japan'; sendMessage()" class="w-full text-left bg-white bg-opacity-10 rounded-lg p-2 text-white text-sm hover:bg-opacity-20 transition-all">
                  "Best time to visit Japan"
                </button>
                <button @click="searchQuery = 'Travel tips for Europe'; sendMessage()" class="w-full text-left bg-white bg-opacity-10 rounded-lg p-2 text-white text-sm hover:bg-opacity-20 transition-all">
                  "Travel tips for Europe"
                </button>
              </div>
            </div>

            <!-- Chat Messages -->
            <div v-for="(message, index) in chatMessages" :key="index" class="flex" :class="message.type === 'user' ? 'justify-end' : 'justify-start'">
              <div class="max-w-[80%] rounded-2xl p-3" :class="message.type === 'user' ? 'bg-white text-gray-800' : 'bg-white bg-opacity-20 text-white'">
                <p class="text-sm">{{ message.content }}</p>
                <span class="text-xs opacity-70 mt-1 block">{{ message.timestamp }}</span>
              </div>
            </div>

            <!-- Loading Message -->
            <div v-if="isSearching" class="flex justify-start">
              <div class="bg-white bg-opacity-20 text-white rounded-2xl p-3">
                <div class="flex items-center space-x-2">
                  <div class="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  <span class="text-sm">Travley is thinking...</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Input Area -->
          <div class="p-4 bg-white bg-opacity-10">
            <div class="flex items-center space-x-2">
              <div class="flex-1 relative">
                <input
                  v-model="searchQuery"
                  @keyup.enter="sendMessage"
                  :disabled="dailySearchCount >= 3 || isSearching"
                  placeholder="Type your travel question here..."
                  class="w-full bg-white bg-opacity-20 text-white placeholder-white placeholder-opacity-70 rounded-xl px-4 py-3 pr-12 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 focus:bg-opacity-30 transition-all"
                />
                <div class="absolute right-3 top-1/2 transform -translate-y-1/2 text-white text-opacity-50">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </div>
              </div>
              <button
                @click="sendMessage"
                :disabled="!searchQuery.trim() || dailySearchCount >= 3 || isSearching"
                class="bg-white text-[#ABBCF4] rounded-xl px-4 py-3 font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:bg-opacity-90 flex items-center space-x-1"
              >
                <span>Send</span>
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              </button>
            </div>
            <p v-if="dailySearchCount >= 3" class="text-white text-opacity-70 text-xs mt-2 text-center">
              Daily search limit reached. Try again tomorrow!
            </p>
            <p v-else class="text-white text-opacity-50 text-xs mt-2 text-center">
              {{ 3 - dailySearchCount }} questions remaining today
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Navbar from '@/components/Navbar.vue';
import TrainSearchPopup from '@/components/TrainSearchPopup.vue';
import MetroSearchPopup from '@/components/MetroSearchPopup.vue';

import HotelsInline from '@/components/HotelsInline.vue';
import RestaurantsInline from '@/components/RestaurantsInline.vue';
import ShoppingInline from '@/components/ShoppingInline.vue';
import BusesInline from '@/components/BusesInline.vue';
import ComingSoonModal from '@/components/ComingSoonModal.vue';
import WeatherPopup from '@/components/WeatherPopup.vue';
import FlightSearchPopup from '@/components/FlightSearchPopup.vue';
import TaskManagementEnhanced from '@/components/TaskManagementEnhanced.vue';
import { getNearbyPlaces } from '@/services/hotelsService';
import { searchRestaurants, searchShopping, searchBuses } from '@/services/searchService';
import { getCurrentLocation, fetchCurrentWeather } from '@/services/weatherService';
import { getTasks } from '@/services/taskService';
import { isAuthenticated } from '@/services/authService';
import { agent } from '@/config/api';
import { useStore } from 'vuex';
import { computed } from 'vue';

// Lucide Icons
import {
  Train,
  Zap,
  Plane,
  Car,
  Bed,
  UtensilsCrossed,
  ShoppingBag,
  Loader2,
  Sun,
  Cloud,
  CloudRain,
  Zap as Lightning,
  Snowflake,
  Wind,
  Eye,
  CheckSquare,
  RefreshCw
} from 'lucide-vue-next';

export default {
  name: 'MobileView',
  components: {
    Navbar,
    TrainSearchPopup,
    MetroSearchPopup,
    FlightSearchPopup,
    HotelsInline,
    RestaurantsInline,
    ShoppingInline,
    BusesInline,
    ComingSoonModal,
    WeatherPopup,
    TaskManagementEnhanced,
    // Lucide Icons
    Train,
    Zap,
    Plane,
    Car,
    Bed,
    UtensilsCrossed,
    ShoppingBag,
    Loader2,
    Sun,
    Cloud,
    CloudRain,
    Lightning,
    Snowflake,
    Wind,
    Eye,
    CheckSquare,
    RefreshCw
  },
  setup() {
    const store = useStore();
    const user = computed(() => store.getters.getUser);

    return {
      user
    };
  },
  data() {
    return {
      activeService: null,
      showComingSoonModal: false,

      // Carousel data
      currentSlide: 0,
      autoSlideInterval: null,

      // Task data
      taskCount: 0,

      // Date and time data
      currentTime: '',
      currentDate: '',
      currentDay: '',
      currentMonth: '',
      timeInterval: null,

      // Hotels data
      hotelsData: null,
      hotelsLoading: false,
      hotelsError: null,

      // Restaurants data
      restaurantsData: null,
      restaurantsLoading: false,
      restaurantsError: null,

      // Shopping data
      shoppingData: null,
      shoppingLoading: false,
      shoppingError: null,

      // Buses data
      busesData: null,
      busesLoading: false,
      busesError: null,

      // Weather data
      currentWeather: null,
      weatherLoading: false,
      weatherError: null,
      showWeatherPopup: false,

      // Flight popup
      showFlightPopup: false,

      // Train popup
      showTrainPopup: false,

      // Metro popup
      showMetroPopup: false,

      // Search Assistant
      showSearchAssistant: false,
      searchQuery: '',
      chatMessages: [],
      isSearching: false,
      dailySearchCount: 0
    }
  },
  computed: {
    formattedDate() {
      // Format as DD.MM (e.g., "13.10")
      const now = new Date();
      const day = now.getDate().toString().padStart(2, '0');
      const month = (now.getMonth() + 1).toString().padStart(2, '0');
      return `${day}.${month}`;
    },
    formattedMonth() {
      // Format as short month name (e.g., "OCT")
      const now = new Date();
      return now.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
    },
    userFirstName() {
      // Extract first name from user data
      if (this.user && this.user.name) {
        return this.user.name.split(' ')[0];
      }
      return 'Traveler'; // Fallback if no user data
    }
  },
  mounted() {
    // Initialize time and date
    this.updateDateTime();
    // Update time every second
    this.timeInterval = setInterval(this.updateDateTime, 1000);

    this.startAutoSlide();
    this.fetchTaskCount();
    this.fetchWeather();
    this.loadDailySearchCount();
  },
  beforeUnmount() {
    this.stopAutoSlide();
    // Clear time interval
    if (this.timeInterval) {
      clearInterval(this.timeInterval);
      this.timeInterval = null;
    }
  },
  methods: {
    updateDateTime() {
      const now = new Date();

      // Format time (12-hour format with AM/PM)
      this.currentTime = now.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });

      // Format date (DD)
      this.currentDate = now.getDate().toString();

      // Format day (Today, Monday, Tuesday, etc.)
      const today = new Date();
      const isToday = now.toDateString() === today.toDateString();
      this.currentDay = isToday ? 'Today' : now.toLocaleDateString('en-US', { weekday: 'long' });

      // Format month (January, February, etc.)
      this.currentMonth = now.toLocaleDateString('en-US', { month: 'long' });
    },
    startAutoSlide() {
      this.autoSlideInterval = setInterval(() => {
        this.nextSlide();
      }, 3000); // Auto-slide every 3 seconds
    },
    stopAutoSlide() {
      if (this.autoSlideInterval) {
        clearInterval(this.autoSlideInterval);
        this.autoSlideInterval = null;
      }
    },
    nextSlide() {
      this.currentSlide = (this.currentSlide + 1) % 3; // 3 slides total
    },
    goToSlide(index) {
      this.currentSlide = index;
      // Restart auto-slide when user manually changes slide
      this.stopAutoSlide();
      this.startAutoSlide();
    },
    async openService(serviceName) {
      // Add tactile feedback delay
      await this.addTactileFeedback();

      // Handle flights, train, and metro separately with their own popups
      if (serviceName === 'flights') {
        this.showFlightPopup = true;
        return;
      }

      if (serviceName === 'train') {
        this.showTrainPopup = true;
        return;
      }

      if (serviceName === 'metro') {
        this.showMetroPopup = true;
        return;
      }

      this.activeService = serviceName;

      // Fetch data if not already loaded
      switch (serviceName) {
        case 'hotels':
          if (!this.hotelsData && !this.hotelsLoading) {
            await this.fetchHotels();
          }
          break;
        case 'restaurants':
          if (!this.restaurantsData && !this.restaurantsLoading) {
            await this.fetchRestaurants();
          }
          break;
        case 'shopping':
          if (!this.shoppingData && !this.shoppingLoading) {
            await this.fetchShopping();
          }
          break;
        case 'buses':
          if (!this.busesData && !this.busesLoading) {
            await this.fetchBuses();
          }
          break;
      }
    },
    closeService() {
      this.activeService = null;
    },
    async openMap() {
      // Add tactile feedback delay
      await this.addTactileFeedback();
      // Navigate to map page
      this.$router.push('/map');
    },

    async navigateToTasks() {
      // Add tactile feedback delay
      await this.addTactileFeedback();
      // Navigate to tasks page
      window.open('https://task.travley.in', '_blank');
      this.closeService(); // Close the modal after opening the link
    },

    // Add tactile feedback for button presses
    addTactileFeedback(event) {
      return new Promise(resolve => {
        // Add visual feedback class if event target is provided
        if (event && event.currentTarget) {
          event.currentTarget.classList.add('tactile-feedback');
          setTimeout(() => {
            event.currentTarget.classList.remove('tactile-feedback');
          }, 150);
        }
        setTimeout(resolve, 150); // 150ms delay for tactile feedback
      });
    },

    async fetchHotels() {
      this.hotelsLoading = true;
      this.hotelsError = null;

      try {
        const location = await getCurrentLocation();
        this.hotelsData = await getNearbyPlaces(location.lat, location.lon, 'lodging');
      } catch (error) {
        console.error('Error fetching hotels:', error);
        this.hotelsError = error.message || 'Failed to fetch hotels. Please try again.';
        this.hotelsData = null;
      } finally {
        this.hotelsLoading = false;
      }
    },

    async fetchRestaurants() {
      this.restaurantsLoading = true;
      this.restaurantsError = null;

      try {
        const location = await getCurrentLocation();
        this.restaurantsData = await searchRestaurants(location.lat, location.lon);
      } catch (error) {
        console.error('Error fetching restaurants:', error);
        this.restaurantsError = error.message || 'Failed to fetch restaurants. Please try again.';
        this.restaurantsData = null;
      } finally {
        this.restaurantsLoading = false;
      }
    },

    async fetchShopping() {
      this.shoppingLoading = true;
      this.shoppingError = null;

      try {
        const location = await getCurrentLocation();
        this.shoppingData = await searchShopping(location.lat, location.lon);
      } catch (error) {
        console.error('Error fetching shopping:', error);
        this.shoppingError = error.message || 'Failed to fetch shopping places. Please try again.';
        this.shoppingData = null;
      } finally {
        this.shoppingLoading = false;
      }
    },

    async fetchBuses() {
      this.busesLoading = true;
      this.busesError = null;

      try {
        const location = await getCurrentLocation();
        this.busesData = await searchBuses(location.lat, location.lon);
      } catch (error) {
        console.error('Error fetching buses:', error);
        this.busesError = error.message || 'Failed to fetch bus stops. Please try again.';
        this.busesData = null;
      } finally {
        this.busesLoading = false;
      }
    },

    async fetchTaskCount() {
      if (!isAuthenticated()) {
        this.taskCount = 0;
        return;
      }

      try {
        // Check cache first
        const cachedData = this.getTasksFromCache();
        if (cachedData) {
          this.taskCount = cachedData.total;
          return;
        }

        // Fetch from API if no cache
        const response = await getTasks(50, 0);
        if (response.success) {
          const tasks = response.tasks;
          const total = tasks.length;
          this.taskCount = total;

          // Cache the data
          this.cacheTasksData({
            tasks,
            total,
            timestamp: Date.now()
          });
        } else {
          this.taskCount = 0;
        }
      } catch (error) {
        console.error('Error fetching task count:', error);
        this.taskCount = 0;
      }
    },

    async fetchWeather() {
      this.weatherLoading = true;
      this.weatherError = null;

      try {
        const location = await getCurrentLocation();
        const weather = await fetchCurrentWeather(location.lat, location.lon);
        this.currentWeather = weather;
      } catch (error) {
        console.error('Error fetching weather:', error);
        this.weatherError = error.message || 'Failed to fetch weather data';
        this.currentWeather = null;
      } finally {
        this.weatherLoading = false;
      }
    },

    getWeatherIcon(weatherMain) {
      const iconMap = {
        'Clear': 'Sun',
        'Clouds': 'Cloud',
        'Rain': 'CloudRain',
        'Drizzle': 'CloudRain',
        'Thunderstorm': 'Lightning',
        'Snow': 'Snowflake',
        'Mist': 'Eye',
        'Smoke': 'Eye',
        'Haze': 'Eye',
        'Dust': 'Eye',
        'Fog': 'Eye',
        'Sand': 'Eye',
        'Ash': 'Eye',
        'Squall': 'Wind',
        'Tornado': 'Wind'
      };
      return iconMap[weatherMain] || 'Cloud';
    },

    async openWeatherPopup(event) {
      event.stopPropagation(); // Prevent triggering parent click events
      // Add tactile feedback delay
      await this.addTactileFeedback();
      this.showWeatherPopup = true;
    },

    closeWeatherPopup() {
      this.showWeatherPopup = false;
    },

    closeFlightPopup() {
      this.showFlightPopup = false;
    },

    handleFlightSearch() {
      this.showFlightPopup = false;
      this.showComingSoonModal = true;
    },

    closeTrainPopup() {
      this.showTrainPopup = false;
    },

    closeMetroPopup() {
      this.showMetroPopup = false;
    },

    // Search Assistant Methods
    async openSearchAssistant() {
      await this.addTactileFeedback();
      this.loadDailySearchCount();
      this.showSearchAssistant = true;
    },

    closeSearchAssistant() {
      this.showSearchAssistant = false;
      this.searchQuery = '';
    },

    loadDailySearchCount() {
      const today = new Date().toDateString();
      const stored = localStorage.getItem('travley_search_count');

      if (stored) {
        const data = JSON.parse(stored);
        if (data.date === today) {
          this.dailySearchCount = data.count;
        } else {
          this.dailySearchCount = 0;
          localStorage.setItem('travley_search_count', JSON.stringify({ date: today, count: 0 }));
        }
      } else {
        this.dailySearchCount = 0;
        localStorage.setItem('travley_search_count', JSON.stringify({ date: today, count: 0 }));
      }
    },

    incrementSearchCount() {
      const today = new Date().toDateString();
      this.dailySearchCount++;
      localStorage.setItem('travley_search_count', JSON.stringify({
        date: today,
        count: this.dailySearchCount
      }));
    },

    async sendMessage() {
      if (!this.searchQuery.trim() || this.dailySearchCount >= 3 || this.isSearching) {
        return;
      }

      const userMessage = {
        type: 'user',
        content: this.searchQuery.trim(),
        timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
      };

      this.chatMessages.push(userMessage);
      const query = this.searchQuery.trim();
      this.searchQuery = '';
      this.isSearching = true;

      try {
        // Call agent worker
        const response = await this.callAgentWorker(query);

        const assistantMessage = {
          type: 'assistant',
          content: response,
          timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
        };

        this.chatMessages.push(assistantMessage);
        this.incrementSearchCount();
      } catch (error) {
        console.error('Search assistant error:', error);

        const errorMessage = {
          type: 'assistant',
          content: 'Sorry, I encountered an error while processing your request. Please try again later.',
          timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
        };

        this.chatMessages.push(errorMessage);
      } finally {
        this.isSearching = false;
      }
    },

    async callAgentWorker(query) {
      // Prepare context data
      const context = {
        user: {
          name: this.user?.name || 'Traveler',
          email: this.user?.email || null
        },
        weather: this.currentWeather,
        taskCount: this.taskCount,
        location: 'User location' // We'll get this from localStorage if available
      };

      try {
        const response = await fetch(`${agent}/search`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            query: query,
            context: context
          })
        });

        if (!response.ok) {
          throw new Error('Failed to get response from Travley assistant');
        }

        const data = await response.json();
        return data.response || 'Sorry, I could not process your request at the moment.';
      } catch (error) {
        console.error('Agent worker error:', error);

        // Fallback response for testing
        return this.generateFallbackResponse(query, context);
      }
    },

    generateFallbackResponse(query, context) {
      const userName = context.user?.name || 'Traveler';
      const lowerQuery = query.toLowerCase();

      // Simple pattern matching for common travel queries
      if (lowerQuery.includes('paris')) {
        return `Hi ${userName}! Paris is the capital of France and one of the world's most popular travel destinations. Known for the Eiffel Tower, Louvre Museum, and Notre-Dame Cathedral, Paris offers incredible art, cuisine, and culture. The best time to visit is during spring (April-June) or fall (September-November) when the weather is pleasant and crowds are smaller. Use Travley's advanced maps to explore Paris and find the best nearby restaurants and attractions!`;
      } else if (lowerQuery.includes('japan')) {
        return `Hello ${userName}! Japan is a fascinating destination that blends ancient traditions with modern innovation. The best time to visit is during spring (March-May) for cherry blossoms or fall (September-November) for beautiful autumn colors. Don't miss Tokyo, Kyoto, and Mount Fuji! Travley's transportation planning can help you navigate Japan's excellent train system efficiently.`;
      } else if (lowerQuery.includes('europe')) {
        return `Hi ${userName}! Europe offers incredible diversity in a relatively small area. Some general tips: get a Eurail pass for train travel, pack light, learn basic phrases in local languages, and try local cuisines. Popular destinations include Paris, Rome, Barcelona, Amsterdam, and Prague. Travley's superior mapping technology can help you navigate European cities better than Google Maps!`;
      } else if (lowerQuery.includes('weather') && context.weather) {
        const temp = Math.round(context.weather.main?.temp || 20);
        return `Based on your current weather (${temp}°C) from Travley's integrated weather system, I'd recommend checking the forecast for your destination. Weather can greatly impact your travel experience, so pack accordingly! Travley provides real-time weather updates and 7-day forecasts for all your travel destinations.`;
      } else if (lowerQuery.includes('task') && context.taskCount > 0) {
        return `I see you have ${context.taskCount} tasks in your Travley task manager. Don't forget to plan your travel tasks and set reminders for important travel preparations! Travley's task management system is perfect for organizing your entire trip from booking to packing.`;
      } else if (lowerQuery.includes('travley') || lowerQuery.includes('features') || lowerQuery.includes('platform')) {
        return `Hi ${userName}! Travley is your superior mapping and travel platform that outperforms Google Maps and Apple Maps. We offer advanced mapping technology, real-time weather integration, transportation planning with train/metro schedules, smart nearby search for hotels and restaurants, and comprehensive travel planning tools. Our platform provides 99.9% uptime with lightning-fast response times. What would you like to explore today?`;
      } else if (lowerQuery.includes('hotel') || lowerQuery.includes('accommodation')) {
        return `Looking for hotels, ${userName}? Travley's smart nearby search provides more accurate results than traditional mapping services. We can help you find the perfect accommodation based on your location, budget, and preferences. Use Travley's advanced maps to explore hotel locations and nearby attractions!`;
      } else if (lowerQuery.includes('restaurant') || lowerQuery.includes('food') || lowerQuery.includes('dining')) {
        return `Hungry for local cuisine, ${userName}? Travley's intelligent search algorithm helps you discover the best restaurants and dining options nearby. Our platform provides more accurate results than Google Maps, with detailed information about local eateries, cafes, and food markets. Bon appétit!`;
      } else {
        return `Hi ${userName}! I'm Travley, your AI travel assistant powered by Travley's superior mapping and travel platform. I can help you with travel destinations, planning tips, weather forecasts, transportation routes, nearby search for hotels and restaurants, and much more. Travley offers advanced features that outperform traditional mapping services. What travel adventure can I help you plan today?`;
      }
    },

    // Task cache management methods
    getTasksFromCache() {
      try {
        const cached = localStorage.getItem('tasks_cache');
        if (!cached) return null;

        const data = JSON.parse(cached);
        const now = Date.now();
        const cacheAge = now - data.timestamp;
        const cacheExpiry = 30 * 60 * 1000; // 30 minutes

        if (cacheAge > cacheExpiry) {
          localStorage.removeItem('tasks_cache');
          return null;
        }

        return data;
      } catch (error) {
        console.error('Error reading tasks cache:', error);
        localStorage.removeItem('tasks_cache');
        return null;
      }
    },

    cacheTasksData(data) {
      try {
        localStorage.setItem('tasks_cache', JSON.stringify(data));
      } catch (error) {
        console.error('Error caching tasks data:', error);
      }
    },

    clearTasksCache() {
      localStorage.removeItem('tasks_cache');
    },

    async refreshTasksData() {
      this.clearTasksCache();
      await this.fetchTaskCount();
      // Emit event to refresh TaskManagementEnhanced component if it's open
      this.$nextTick(() => {
        if (this.activeService === 'tasks') {
          // Force refresh of TaskManagementEnhanced component
          const taskComponent = this.$refs.taskManagement;
          if (taskComponent && taskComponent.loadTasks) {
            taskComponent.loadTasks(true);
          }
        }
      });
    },

    // Event handlers for task operations
    onTaskCreated() {
      // Clear cache and refresh count when a new task is created
      this.clearTasksCache();
      this.fetchTaskCount();
    },

    onTaskDeleted() {
      // Clear cache and refresh count when a task is deleted
      this.clearTasksCache();
      this.fetchTaskCount();
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@100;200;300;400;500;600;700;800;900&display=swap');

/* Apply Poppins font to the entire component */
* {
  font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
}

/* Ensure proper aspect ratios and responsive design */
.aspect-square {
  aspect-ratio: 1 / 1;
}

/* Neumorphic Design System */
.neumorphic-card {
  background: #F3F4F6;
  box-shadow:
    8px 8px 16px #d1d5db,
    -8px -8px 16px #ffffff;
}

.neumorphic-tile {
  background: #F3F4F6;
  box-shadow:
    6px 6px 12px #d1d5db,
    -6px -6px 12px #ffffff;
}

.neumorphic-tile:hover {
  box-shadow:
    4px 4px 8px #d1d5db,
    -4px -4px 8px #ffffff;
  transform: translateY(-1px);
}

.neumorphic-tile:active {
  box-shadow:
    inset 4px 4px 8px #d1d5db,
    inset -4px -4px 8px #ffffff;
  transform: translateY(1px);
  transition: all 0.15s ease-in-out;
}

.neumorphic-icon {
  box-shadow:
    3px 3px 6px #d1d5db,
    -3px -3px 6px #ffffff;
}

.neumorphic-icon:active {
  box-shadow:
    inset 2px 2px 4px #d1d5db,
    inset -2px -2px 4px #ffffff;
}

.neumorphic-slide:hover {
  background: rgba(255, 255, 255, 0.1);
}

.neumorphic-slide:active {
  background: rgba(0, 0, 0, 0.05);
  transform: scale(0.98);
  transition: all 0.15s ease-in-out;
}

/* Smooth transitions */
* {
  transition-property: background-color, border-color, color, fill, stroke, opacity, box-shadow, transform;
  transition-duration: 200ms;
  transition-timing-function: ease-in-out;
}

/* Tactile feedback animation */
.tactile-feedback {
  animation: tactilePress 0.15s ease-in-out;
}

@keyframes tactilePress {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(0.95);
  }
  100% {
    transform: scale(1);
  }
}

/* Car Animation */
.car-animation {
  animation: carMove 3s ease-in-out infinite;
}

@keyframes carMove {
  0%, 100% {
    transform: translateX(0) rotate(0deg);
  }
  25% {
    transform: translateX(4px) rotate(2deg);
  }
  50% {
    transform: translateX(8px) rotate(0deg);
  }
  75% {
    transform: translateX(4px) rotate(-2deg);
  }
}
</style>
